<div class="page main h-100 w-100 d-flex flex-column">
  <!-- Toolbar with business selector -->
  <app-toolbar type="menu" class="not-mobile"
               leftAction="Back"
               (backClick)="backClick()">
  </app-toolbar>
  <app-mobile-toolbar class="only-mobile"
                      [showMenu]="true"
                      leftAction="Back"
                      (backClick)="backClick()">
  </app-mobile-toolbar>

  <div class="container flex-fill d-flex flex-column">
    <div class="page-header">
      <h2 class="page-title">Import Leads from CSV</h2>
      <button mat-stroked-button color="primary" (click)="openExample()">
        <mat-icon>help_outline</mat-icon>
        View Example
      </button>
    </div>

    <!-- Initial Upload State -->
    <div *ngIf="uploadState === 'initial'" class="upload-section">
      <!-- Placeholder Options -->
      <div class="placeholder-options">
        <h3>Import Options</h3>
        <div class="checkbox-group">
          <mat-checkbox [(ngModel)]="allowMissingPhone">
            Allow missing phone numbers (use placeholder: 000-000-0000)
          </mat-checkbox>
          <mat-checkbox [(ngModel)]="allowMissingEmail">
            Allow missing emails (use placeholder: noemail@placeholder.com)
          </mat-checkbox>
        </div>
      </div>

      <div class="drag-drop-area"
           appDragNDrop
           (fileDropped)="fileUploaded($event)"
           (dragover)="onDragOver()"
           (dragleave)="onDragLeave()"
           [class.dragover]="fileOverDrag">
        <mat-icon class="upload-icon">cloud_upload</mat-icon>
        <h3>Drag & Drop your CSV file here</h3>
        <p>We support CSV, XLS, and XLSX formats</p>
        <div class="divider">
          <div class="line"></div>
          <div>Or</div>
          <div class="line"></div>
        </div>
        <button mat-raised-button color="primary" (click)="fileInput.click()">
          Choose File
        </button>
        <input #fileInput type="file" class="hide"
               accept=".csv,.xls,.xlsx"
               (change)="fileUploaded($any($event.target).files[0])" />
      </div>
    </div>

    <!-- Preview State -->
    <div *ngIf="uploadState === 'preview'" class="preview-section">
      <!-- Error Summary -->
      <div *ngIf="invalidRows.length > 0" class="error-summary">
        <h3><mat-icon>error_outline</mat-icon> Validation Issues</h3>
        <p><strong>{{ invalidRows.length }}</strong> rows have errors and will not be imported</p>
        <div class="error-list">
          <div *ngFor="let row of invalidRows.slice(0, 10)" class="error-item">
            <strong>Row {{ row.rowNumber }}:</strong>
            <span>{{ getErrorMessages(row) }}</span>
          </div>
          <p *ngIf="invalidRows.length > 10" class="more-errors">
            ...and {{ invalidRows.length - 10 }} more rows with errors
          </p>
        </div>
      </div>

      <!-- Preview Table -->
      <div class="preview-table-container">
        <h3>Preview ({{ validRows.length }} valid rows)</h3>
        <div class="table-wrapper">
          <table mat-table [dataSource]="validRows" class="preview-table">

            <ng-container matColumnDef="rowNumber">
              <th mat-header-cell *matHeaderCellDef>Row</th>
              <td mat-cell *matCellDef="let row">{{ row.rowNumber }}</td>
            </ng-container>

            <ng-container matColumnDef="businessName">
              <th mat-header-cell *matHeaderCellDef>Business Name</th>
              <td mat-cell *matCellDef="let row">{{ row.businessName }}</td>
            </ng-container>

            <ng-container matColumnDef="phoneNumber">
              <th mat-header-cell *matHeaderCellDef>Phone</th>
              <td mat-cell *matCellDef="let row">{{ row.phoneNumber }}</td>
            </ng-container>

            <ng-container matColumnDef="email">
              <th mat-header-cell *matHeaderCellDef>Email</th>
              <td mat-cell *matCellDef="let row">{{ row.email }}</td>
            </ng-container>

            <ng-container matColumnDef="streetAddress">
              <th mat-header-cell *matHeaderCellDef>Street Address</th>
              <td mat-cell *matCellDef="let row">
                {{ row.parsedAddress?.streetNumber }}
                {{ row.parsedAddress?.streetName }}
              </td>
            </ng-container>

            <ng-container matColumnDef="unit">
              <th mat-header-cell *matHeaderCellDef>Unit</th>
              <td mat-cell *matCellDef="let row">{{ row.unit }}</td>
            </ng-container>

            <ng-container matColumnDef="city">
              <th mat-header-cell *matHeaderCellDef>City</th>
              <td mat-cell *matCellDef="let row">{{ row.city }}</td>
            </ng-container>

            <ng-container matColumnDef="state">
              <th mat-header-cell *matHeaderCellDef>State</th>
              <td mat-cell *matCellDef="let row">{{ row.state }}</td>
            </ng-container>

            <ng-container matColumnDef="zip">
              <th mat-header-cell *matHeaderCellDef>ZIP</th>
              <td mat-cell *matCellDef="let row">{{ row.zip }}</td>
            </ng-container>

            <ng-container matColumnDef="jobType">
              <th mat-header-cell *matHeaderCellDef>Job Type</th>
              <td mat-cell *matCellDef="let row">{{ row.jobType }}</td>
            </ng-container>

            <ng-container matColumnDef="notes">
              <th mat-header-cell *matHeaderCellDef>Notes</th>
              <td mat-cell *matCellDef="let row">{{ row.notes }}</td>
            </ng-container>

            <ng-container matColumnDef="warnings">
              <th mat-header-cell *matHeaderCellDef>Warnings</th>
              <td mat-cell *matCellDef="let row">
                <span *ngIf="row.warnings.length > 0" class="warning-badge"
                      [matTooltip]="getWarningMessages(row)">
                  {{ row.warnings.length }}
                </span>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
          </table>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <button mat-button (click)="cancelUpload()">Cancel</button>
        <button mat-raised-button color="primary"
                (click)="importLeads()"
                [disabled]="validRows.length === 0">
          Import {{ validRows.length }} Leads
        </button>
      </div>
    </div>

    <!-- Importing State -->
    <div *ngIf="uploadState === 'importing'" class="importing-section">
      <mat-icon class="progress-icon">sync</mat-icon>
      <h3>Importing Leads...</h3>
      <mat-progress-bar mode="determinate"
                        [value]="(importProgress.processed / importProgress.total) * 100">
      </mat-progress-bar>
      <p class="progress-text">
        {{ importProgress.processed }} / {{ importProgress.total }} processed
        ({{ importProgress.succeeded }} succeeded, {{ importProgress.failed }} failed)
      </p>
      <p *ngIf="importProgress.currentRow" class="current-row">
        Processing row {{ importProgress.currentRow }}...
      </p>
    </div>

    <!-- Complete State -->
    <div *ngIf="uploadState === 'complete'" class="complete-section">
      <mat-icon class="success-icon">check_circle</mat-icon>
      <h3>Import Complete!</h3>
      <p class="success-text">Successfully imported <strong>{{ importProgress.succeeded }}</strong> leads</p>
      <p *ngIf="importProgress.failed > 0" class="error-text">
        <mat-icon>error_outline</mat-icon> {{ importProgress.failed }} leads failed to import
      </p>

      <div class="action-buttons">
        <button mat-button (click)="router.navigate(['/lobby/schedule/leads'])">
          View Leads
        </button>
        <button mat-raised-button color="primary"
                (click)="uploadAnotherFile()">
          Upload Another File
        </button>
      </div>
    </div>
  </div>
</div>
